import { Candle, OptionRow, SessionMeta, LiquidityZone, AppSettings } from '../../../../types';
import {
  calcVWAP, calcSessionLevels, detectSwingHighs, detectSwingLows,
  findEqualLevels, nearestRoundNumbers, detectGapEdges
} from '../../../../utils/calc';
import type { DarkPoolPrintCluster } from '../../../institutionalFlow/clusters';

// ─── Liquidity taxonomy ───────────────────────────────────────────────────────
// Each zone now carries a category so downstream engines can prioritize correctly.
// structural_liquidity: prior-day levels, equal H/L, major swings — highest priority
// reference_level:      OR edges, gap edges — meaningful but secondary
// mean_anchor:          VWAP — dynamic benchmark, not a hard level
// psychological_level:  round numbers — soft magnet
// gamma_level:          high-OI option strikes — dealer hedging friction

export type LiquidityCategory =
  | 'structural_liquidity'
  | 'reference_level'
  | 'mean_anchor'
  | 'psychological_level'
  | 'gamma_level';

declare module '../../../../types' {
  interface LiquidityZone {
    category?: LiquidityCategory;
    /** Phase 2 v1: dark-pool print cluster confirms this zone. */
    darkPoolSupport?: boolean;
    /** Phase 2 v1: number of off-exchange prints at or near this zone within the cluster window. */
    darkPoolPrintCount?: number;
    /** Phase 2 v1: total off-exchange share volume confirming this zone. */
    darkPoolShares?: number;
  }
}

export function buildLiquidityZones(
  candles: Candle[],
  options: OptionRow[] | null,
  meta: SessionMeta,
  settings: AppSettings,
  /**
   * Phase 2 v1 (liquidity multi-source): off-exchange print clusters from
   * the institutional flow runtime. When present, zones within tolerance
   * of a significant cluster get darkPoolSupport=true, the sig is bumped
   * one tier where eligible, and the reason text records the confirmation.
   *
   * Behavior is ADDITIVE ONLY in v1 -- no orphan zones are created from
   * clusters alone. ThetaData-driven structural and gamma logic is
   * unchanged; clusters serve as confirmation evidence only.
   *
   * Passing null/undefined is the no-op default and matches pre-v1
   * behavior, so callers without cluster context (server-side rebuilds,
   * non-browser code paths) need no changes.
   */
  printClusters: DarkPoolPrintCluster[] | null = null
): LiquidityZone[] {
  if (!candles.length) return [];

  const tol = settings.tolerance / 100;
  const curPrice = candles[candles.length - 1].close;
  const vwapArr = calcVWAP(candles);
  const curVWAP = vwapArr[vwapArr.length - 1];
  const levels = calcSessionLevels(candles);

  const raw: LiquidityZone[] = [];

  const add = (
    price: number, label: string, type: LiquidityZone['type'],
    sig: LiquidityZone['sig'], reason: string, source: LiquidityZone['source'],
    category: LiquidityCategory
  ) => {
    if (price <= 0 || isNaN(price)) return;
    (raw as Array<LiquidityZone & { category: LiquidityCategory }>).push({
      price, label, type, sig, reason, source,
      distPct: Math.abs(price - curPrice) / curPrice * 100,
      category,
    });
  };

  // Session VWAP — mean anchor, not a structural level
  add(curVWAP, 'Session VWAP', 'key', 'High',
    'Institutional benchmark — algo fills cluster near VWAP throughout session',
    'structure', 'mean_anchor');

  // Opening range — reference levels (degrade in importance after first hour)
  if (levels.orHigh > 0) add(levels.orHigh, 'Opening Range High', 'resistance', 'High',
    'OR high traps aggressive breakout buyers on failure; stop-loss cluster above',
    'structure', 'reference_level');
  if (levels.orLow > 0) add(levels.orLow, 'Opening Range Low', 'support', 'High',
    'OR low contains early shorts; liquidity pool below for sweep attempts',
    'structure', 'reference_level');

  // Prior day levels — genuine structural liquidity (highest priority)
  if (meta.prevHigh) add(meta.prevHigh, 'Prior Day High', 'resistance', 'High',
    'Largest intraday stop cluster — prior session shorts stop here; breakout target',
    'meta', 'structural_liquidity');
  if (meta.prevLow) add(meta.prevLow, 'Prior Day Low', 'support', 'High',
    'Prior session longs stop here; breakdown continuation trigger',
    'meta', 'structural_liquidity');
  if (meta.prevClose) add(meta.prevClose, 'Prior Day Close', 'key', 'Medium',
    'Benchmark for gap assessment and institutional mean reference',
    'meta', 'reference_level');

  // Premarket extremes — reference level
  if (meta.pmHigh) add(meta.pmHigh, 'Premarket High', 'resistance', 'Medium',
    'Premarket high — often revisited during regular session', 'meta', 'reference_level');
  if (meta.pmLow) add(meta.pmLow, 'Premarket Low', 'support', 'Medium',
    'Premarket low — unfilled activity creates intraday magnet', 'meta', 'reference_level');

  // Swing highs/lows — structural when formed by significant pivots
  if (candles.length >= settings.minSwingBars * 3) {
    const swH = detectSwingHighs(candles, settings.minSwingBars);
    const swL = detectSwingLows(candles, settings.minSwingBars);

    swH.slice(-4).forEach(s => add(s.price, 'Swing High', 'resistance', 'Medium',
      'Recent pivot high — sell orders and stop-losses rest above',
      'structure', 'structural_liquidity'));
    swL.slice(-4).forEach(s => add(s.price, 'Swing Low', 'support', 'Medium',
      'Recent pivot low — buy orders and stop-losses rest below',
      'structure', 'structural_liquidity'));

    // Equal highs/lows — densest structural liquidity
    findEqualLevels(swH, tol).forEach(e => add(e.price,
      `Equal Highs ×${e.count}`, 'resistance', 'High',
      `${e.count} equal highs = dense liquidity pool above — high-probability sweep target`,
      'structure', 'structural_liquidity'));
    findEqualLevels(swL, tol).forEach(e => add(e.price,
      `Equal Lows ×${e.count}`, 'support', 'High',
      `${e.count} equal lows = dense liquidity pool below — high-probability sweep target`,
      'structure', 'structural_liquidity'));
  }

  // Round numbers — psychological only
  nearestRoundNumbers(curPrice).forEach(rn =>
    add(rn, `Round Number ${rn}`, 'key', 'Medium',
      'Psychological level — retail stops + OTC orders cluster at round numbers',
      'round', 'psychological_level')
  );

  // Gap edges — reference level
  detectGapEdges(candles, meta).forEach(g =>
    add(g, 'Gap Edge (Prior Close)', 'key', 'Medium',
      'Unfilled price gap — acts as magnetic level for mean-reversion algorithms',
      'meta', 'reference_level')
  );

  // Options strikes — gamma level
  if (options?.length) {
    const maxCombined = Math.max(...options.map(o => o.callOI + o.putOI));
    const threshold = maxCombined * 0.35;
    options
      .filter(o => (o.callOI + o.putOI) >= threshold)
      .forEach(o => {
        const total = (o.callOI + o.putOI) / 1000;
        const type: LiquidityZone['type'] = o.callOI > o.putOI * 1.5 ? 'resistance'
          : o.putOI > o.callOI * 1.5 ? 'support' : 'key';
        const reason = type === 'key'
          ? `Balanced OI at strike — dealer hedging creates friction; max-pain magnet. Total OI: ${total.toFixed(0)}K`
          : type === 'resistance'
          ? `Call-heavy strike (${(o.callOI/1000).toFixed(0)}K calls) — gamma hedging creates supply above`
          : `Put-heavy strike (${(o.putOI/1000).toFixed(0)}K puts) — gamma hedging creates demand below`;
        add(o.strike, `Strike ${o.strike} (${total.toFixed(0)}K OI)`, type, 'High',
          reason, 'options', 'gamma_level');
      });
  }

  // ── Deduplication — preserve structural levels over weaker reference levels ─
  // Category priority: structural_liquidity > gamma_level > reference_level > mean_anchor > psychological_level
  const CATEGORY_PRIORITY: Record<LiquidityCategory, number> = {
    structural_liquidity: 5,
    gamma_level:          4,
    reference_level:      3,
    mean_anchor:          2,
    psychological_level:  1,
  };

  const SIG_PRIORITY: Record<string, number> = { High: 3, Medium: 2, Low: 1 };

  // Sort: structural first, then sig, then distance
  const prioritized = [...raw].sort((a, b) => {
    const ca = (a as LiquidityZone & { category?: LiquidityCategory }).category;
    const cb = (b as LiquidityZone & { category?: LiquidityCategory }).category;
    const catDiff = (CATEGORY_PRIORITY[cb ?? 'reference_level'] ?? 0) -
                    (CATEGORY_PRIORITY[ca ?? 'reference_level'] ?? 0);
    if (catDiff !== 0) return catDiff;
    return SIG_PRIORITY[b.sig] - SIG_PRIORITY[a.sig];
  });

  const deduped: LiquidityZone[] = [];
  const used = new Set<number>();

  prioritized.forEach((z, i) => {
    if (used.has(i)) return;
    const cat = (z as LiquidityZone & { category?: LiquidityCategory }).category;
    const dupes: LiquidityZone[] = [z];
    prioritized.forEach((z2, j) => {
      if (j !== i && !used.has(j)) {
        const cat2 = (z2 as LiquidityZone & { category?: LiquidityCategory }).category;
        // Only merge if same category OR weaker category into stronger one
        const catOk = cat === cat2 ||
          (CATEGORY_PRIORITY[cat ?? 'reference_level'] >= CATEGORY_PRIORITY[cat2 ?? 'reference_level']);
        if (catOk && Math.abs(z.price - z2.price) / z.price < tol * 0.6) {
          dupes.push(z2);
          used.add(j);
        }
      }
    });
    const merged: LiquidityZone = {
      ...z,
      label: dupes.length > 1
        ? dupes.map(d => d.label).join(' / ').substring(0, 60)
        : z.label,
      mergedWith: dupes.length > 1
        ? dupes.slice(1).map(d => d.label).join(', ')
        : undefined,
    };
    deduped.push(merged);
    used.add(i);
  });

  // ── Phase 2 v1: dark-pool cluster confirmation pass ──────────────────────
  //
  // For each significant off-exchange print cluster, find the nearest
  // existing zone within (tol * 0.5) and tag it as dark-pool supported.
  // Bump sig one tier where eligible (Low -> Medium, Medium -> High); High
  // stays High because there's no higher tier. Multiple clusters can each
  // tag different zones; the same zone can accumulate prints/shares from
  // overlapping clusters (rare given dedup, but harmless).
  //
  // Reason text is appended (not replaced) so the structural rationale
  // for the zone remains visible. Boundary case: when sig is already
  // 'High', we still mark darkPoolSupport and accumulate counts -- the
  // tier doesn't change but downstream consumers (prompt, AI, future
  // confluence) can still see the confirmation.
  if (Array.isArray(printClusters) && printClusters.length > 0) {
    const sigBump: Record<string, LiquidityZone['sig']> = {
      Low:    'Medium',
      Medium: 'High',
      High:   'High',
    };
    for (const cluster of printClusters) {
      if (!cluster || !isFinite(cluster.price) || cluster.price <= 0) continue;
      // Find the closest zone within (tol * 0.5) of the cluster price.
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < deduped.length; i++) {
        const z = deduped[i];
        const dist = Math.abs(z.price - cluster.price) / cluster.price;
        if (dist <= tol * 0.5 && dist < bestDist) {
          bestDist = dist;
          bestIdx  = i;
        }
      }
      if (bestIdx < 0) continue;
      const z = deduped[bestIdx];
      const ze = z as LiquidityZone & {
        darkPoolSupport?: boolean;
        darkPoolPrintCount?: number;
        darkPoolShares?: number;
      };
      ze.darkPoolSupport    = true;
      ze.darkPoolPrintCount = (ze.darkPoolPrintCount ?? 0) + cluster.printCount;
      ze.darkPoolShares     = (ze.darkPoolShares     ?? 0) + cluster.totalShares;
      // Bump sig only on the FIRST cluster attribution to a zone -- avoid
      // double-bumping if multiple clusters land near the same level.
      // Detect first attribution by checking whether reason text was
      // already extended.
      const alreadyAnnotated = /off-exchange/i.test(z.reason);
      if (!alreadyAnnotated) {
        z.sig = sigBump[z.sig] ?? z.sig;
      }
      z.reason = `${z.reason} | Confirmed by ${ze.darkPoolPrintCount} off-exchange prints (${ze.darkPoolShares.toLocaleString()} shares) within the last 5min window.`;
    }
  }

  return deduped.sort((a, b) => b.price - a.price);
}
